import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'preact/hooks';

import {
  UnableToResolveVariantError,
  useProductDetails,
  useProductPropertyChangeEvents,
} from 'utilities/product';
import {useErrorLogger} from 'utilities/error-logger/hooks';
import {ReportType} from 'utilities/error-logger/types';
import {
  FetchAPIError,
  ForcedError,
  NetworkError,
  QueryStorefrontAPIError,
} from 'utilities/storefront-api-client';
import {useLocation} from 'utilities/location';
import {useShopifyFeatures} from 'utilities/shopify-features';
import {trackFetchingDeliveryPromise} from 'utilities/tracking';

import {InvalidVariantIdError, NoDeliveryEstimateError} from './errors';
import {DeliveryEstimateContextImpl} from './context';

export function useDeliveryEstimate() {
  return useContext(DeliveryEstimateContextImpl);
}

function scheduleSideEffect<TFn extends (...args: any[]) => any>(
  handler: TFn,
  ...params: Parameters<TFn>
) {
  setTimeout(handler, 0, ...params);
}

const forceError = new URLSearchParams(location.search).has('forceError');
const forceErrorAlways = new URLSearchParams(location.search).has(
  'forceErrorAlways',
);
const testInvestigateError = new URLSearchParams(location.search).has(
  'testInvestigateError',
);
let errorCount = 0;

/**
 *
 * @param defaultVariantId
 * @param defaultSellingPlanId
 * @param postalCode
 * @param theme
 */
export function useFetchDeliveryEstimate(
  defaultVariantId: number | undefined,
  defaultSellingPlanId: number | undefined,
  postalCode?: string | null,
) {
  const {reportError} = useErrorLogger();
  const {defaultAddress, setDefaultAddress} = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const {variantId, sellingPlanId, setVariantId, setSellingPlanId} =
    useProductDetails();
  const {deliveryEstimate, setDeliveryEstimate} = useDeliveryEstimate();
  const {accessToken} = useShopifyFeatures();

  const queryDeliveryEstimates =
    window.Shopify.ShopPromise.deliveryPromiseAPIClient;

  const currentVariantId = useRef(variantId);
  currentVariantId.current = variantId;

  useEffect(() => {
    if (defaultVariantId) {
      setVariantId(defaultVariantId);
    }
  }, [defaultVariantId, setVariantId]);

  useEffect(() => {
    if (defaultSellingPlanId) {
      setSellingPlanId(defaultSellingPlanId);
    }
  }, [defaultSellingPlanId, setSellingPlanId]);

  useProductPropertyChangeEvents();

  const queryFactory = useCallback(() => {
    if (!variantId) {
      setDeliveryEstimate(null);
      return;
    }
    let requestId = '';

    setHasError(false);
    setIsLoading(true);

    scheduleSideEffect(trackFetchingDeliveryPromise, {
      variantId,
      postalCodeOverride: postalCode,
    });

    return queryDeliveryEstimates(
      {
        variantId,
        sellingPlanId,
        postalCode,
        defaultPostalCode: undefined,
      },
      accessToken,
    )
      .then((data) => {
        // If the variant has changed, discard the request.
        if (currentVariantId.current !== variantId) {
          return;
        }

        requestId = data?.requestId || '';
        if (forceError || forceErrorAlways) {
          if (forceErrorAlways) {
            throw new ForcedError('Forced error');
          }
          errorCount++;
          // Only throw error on every other request made
          if (errorCount % 2 !== 0) {
            throw new ForcedError('Forced error');
          }
        }
        if (testInvestigateError) {
          throw new TypeError('Test investigate error');
        }

        setDeliveryEstimate({
          deliveryEstimates: data,
          variantId,
        });
      })
      .finally(() => setIsLoading(false))
      .catch((error: unknown) => {
        // If the variant has changed, we'll report the error below, but not
        // update the state of the UI.
        if (currentVariantId.current === variantId) {
          setHasError(true);
          setDeliveryEstimate(null);
        }

        // todo: leave breadcrumbs?
        const loggingParams = `Failed to fetch Delivery Estimates: [variantId=${variantId}] [postalCode=${postalCode}]`;
        switch (true) {
          case error instanceof FetchAPIError:
          case error instanceof NetworkError:
            reportError({
              severity: ReportType.Expected,
              error,
              message: loggingParams,
              requestId,
            });
            break;
          case error instanceof NoDeliveryEstimateError:
            reportError({
              severity: ReportType.Expected,
              error,
              message: loggingParams,
              requestId: (error as NoDeliveryEstimateError).requestId,
            });
            break;
          case error instanceof QueryStorefrontAPIError:
          case error instanceof InvalidVariantIdError:
          case error instanceof UnableToResolveVariantError:
            reportError({
              severity: ReportType.Metric,
              error,
              message: loggingParams,
              requestId,
            });
            break;
          case error instanceof ForcedError:
            // eslint-disable-next-line no-console
            console.log("Forced error, don't report", error);
            break;
          default:
            reportError({
              severity: ReportType.Investigate,
              error,
              message: loggingParams,
              requestId,
            });
        }
      });
  }, [
    variantId,
    postalCode,
    queryDeliveryEstimates,
    sellingPlanId,
    accessToken,
    setDeliveryEstimate,
    reportError,
  ]);

  useEffect(() => {
    queryFactory();
  }, [queryFactory]);

  useEffect(() => {
    if (
      !defaultAddress &&
      deliveryEstimate?.deliveryAddress &&
      !isLoading &&
      !hasError
    ) {
      setDefaultAddress(deliveryEstimate.deliveryAddress);
    }
  }, [
    defaultAddress,
    deliveryEstimate,
    hasError,
    isLoading,
    setDefaultAddress,
  ]);

  return useMemo(
    () => ({deliveryEstimate, isLoading, hasError}),
    [deliveryEstimate, isLoading, hasError],
  );
}
